import React, { useState } from 'react';
import './RadarChart.css';

const AXES = [
  'Jump', 'Squat', 'Lateral', 'Hinge', 'Carry', 'Core',
  'Rotation', 'Horiz. Push', 'Vert. Push', 'Horiz. Pull', 'Vert. Pull', 'Shoulder',
];

export default function RadarChart({ scores = {}, size = 280, className = '' }) {
  const [hoveredAxis, setHoveredAxis] = useState(null);
  const cx = size / 2;
  const cy = size / 2;
  const R = size * 0.35;
  const LABEL_R = size * 0.46;

  function pointAt(r, i) {
    const angleDeg = i * 30 - 90;
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(angleRad),
      y: cy + r * Math.sin(angleRad),
      cos: Math.cos(angleRad),
      sin: Math.sin(angleRad),
    };
  }

  const rings = [0.25, 0.5, 0.75, 1].map((fraction) =>
    AXES.map((_, i) => {
      const p = pointAt(R * fraction, i);
      return `${p.x},${p.y}`;
    }).join(' ')
  );

  const spokes = AXES.map((_, i) => pointAt(R, i));

  const scorePoints = AXES.map((axis, i) => {
    const score = Math.max(0, Math.min(1, scores[axis] ?? 0));
    const p = pointAt(R * score, i);
    return `${p.x},${p.y}`;
  }).join(' ');

  const scoreDots = AXES.map((axis, i) => {
    const score = Math.max(0, Math.min(1, scores[axis] ?? 0));
    return pointAt(R * score, i);
  });

  function textAnchor(cos) {
    if (cos > 0.3) return 'start';
    if (cos < -0.3) return 'end';
    return 'middle';
  }

  function labelDy(sin) {
    if (sin < -0.3) return '-4';
    if (sin > 0.3) return '12';
    return '4';
  }

  return (
    <div className="radar-chart-wrap" style={{ position: 'relative', width: size, maxWidth: '100%', height: size }}>
      <svg
        className={`radar-chart ${className}`}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-label="Radar chart"
      >
      {rings.map((pts, i) => (
        <polygon key={i} className="radar-chart__ring" points={pts} />
      ))}

      {spokes.map((p, i) => (
        <line
          key={i}
          className="radar-chart__spoke"
          x1={cx}
          y1={cy}
          x2={p.x}
          y2={p.y}
        />
      ))}

      <polygon className="radar-chart__area" points={scorePoints} />

      {scoreDots.map((p, i) => (
        <circle key={i} className="radar-chart__dot" cx={p.x} cy={p.y} r={2.5} />
      ))}

      {AXES.map((axis, i) => {
        const p = pointAt(LABEL_R, i);
        return (
          <g
            key={i}
            className="radar-chart__label-group"
            tabIndex={0}
            onMouseEnter={() => setHoveredAxis(i)}
            onMouseLeave={() => setHoveredAxis(null)}
            onFocus={() => setHoveredAxis(i)}
            onBlur={() => setHoveredAxis(null)}
          >
            <circle className="radar-chart__label-hit" cx={p.x} cy={p.y} r={14} />
            <text
              className="radar-chart__label"
              x={p.x}
              y={p.y}
              textAnchor={textAnchor(p.cos)}
              dy={labelDy(p.sin)}
            >
              {axis}
            </text>
          </g>
        );
      })}
      </svg>

      {hoveredAxis !== null && (() => {
        const axis = AXES[hoveredAxis];
        const p = pointAt(LABEL_R, hoveredAxis);
        const offset = 22;
        const tx = p.x + p.cos * offset;
        const ty = p.y + p.sin * offset;
        const score = Math.round((scores[axis] ?? 0) * 100);
        return (
          <div
            className="radar-chart__tooltip"
            role="tooltip"
            style={{ left: `${(tx / size) * 100}%`, top: `${(ty / size) * 100}%` }}
          >
            <strong>{axis}</strong>
            <span>{score}%</span>
          </div>
        );
      })()}
    </div>
  );
}
